import { createSelector } from 'reselect'
import { immer } from 'zustand/middleware/immer'

import { type PagePaths, pages } from '~/app/routes'

import { StoreState } from '../store'
import { BaseFilter, createFilterSliceActions, FilterSliceActions } from './types'

export const surgeonSchedulerLocationFilters = ['practitioner', 'location'] as const
export const surgeonSchedulerPractitionerFilters = ['practitioner', 'status'] as const

export type SurgeonSchedulerFilterType = (typeof surgeonSchedulerLocationFilters)[number] | (typeof surgeonSchedulerPractitionerFilters)[number]

export type SurgeonSchedulerFilter = {
    type: SurgeonSchedulerFilterType
} & BaseFilter

type State = {
    filters: SurgeonSchedulerFilter[]
}

type Actions = {
    actions: FilterSliceActions<SurgeonSchedulerFilterType>
}

export type SurgeonSchedulerFilterSlice = {
    surgeonSchedulerFilters: State & Actions
}

const initialState: State = {
    filters: [],
}

export const createSurgeonSchedulerFilterSlice = immer<SurgeonSchedulerFilterSlice>((set, get) => ({
    surgeonSchedulerFilters: {
        ...initialState,
        actions: createFilterSliceActions<SurgeonSchedulerFilterType, SurgeonSchedulerFilter>('surgeonSchedulerFilters', set, get),
    },
}))

const selectAllFilters = (state: StoreState) => state.surgeonSchedulerFilters.filters

/**
 * The filter types each Bemanning view offers. The slice keeps every view's filters as the planner moves between them.
 * Fagområde has no filters or filter profiles.
 */
export const surgeonSchedulerFiltersByView: Record<PagePaths['SURGEON_SCHEDULER'], readonly SurgeonSchedulerFilterType[]> = {
    [pages.SURGEON_SCHEDULER.views.practitioners.path]: surgeonSchedulerPractitionerFilters,
    [pages.SURGEON_SCHEDULER.views.locations.path]: surgeonSchedulerLocationFilters,
    [pages.SURGEON_SCHEDULER.views.allocations.path]: [],
}

// View specific selector
export const selectSurgeonSchedulerFiltersForView = createSelector(
    selectAllFilters,
    (state: StoreState) => state.app.activeViews.SURGEON_SCHEDULER,
    (filters, activeView) => filters.filter((filter: SurgeonSchedulerFilter) => surgeonSchedulerFiltersByView[activeView].includes(filter.type))
)

// Filter specific selectors
export const selectPractitionerValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: SurgeonSchedulerFilter) => filter.type === 'practitioner').map((filter: SurgeonSchedulerFilter) => filter.value)
)

export const selectLocationValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: SurgeonSchedulerFilter) => filter.type === 'location').map((filter: SurgeonSchedulerFilter) => filter.value)
)

export const selectStatusValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: SurgeonSchedulerFilter) => filter.type === 'status').map((filter: SurgeonSchedulerFilter) => filter.value)
)
